import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import { EXPORT_GROUPINGS, PAGE_FORMATS, getGroupedPositions } from "./pageFormats";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Printer,
  Grid3X3,
  Layers,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileText,
  Image as ImageIcon,
} from "lucide-react";

/**
 * ExportPreviewDialog - Full screen preview before export
 * Supports: Carousel view (default), Grid view, Grouping preview
 */
export function ExportPreviewDialog({
  open,
  onOpenChange,
  pages = [],
  currentPageIndex = 0,
  stageRef,
  documentTitle = "Document",
  pageFormat = "A4",
  pageOrientation = "portrait",
  onExportPDF,
  onExportPNG,
  onExportGrouped,
  onPrint,
}) {
  const [viewMode, setViewMode] = useState("carousel"); // carousel, grid
  const [activePageIndex, setActivePageIndex] = useState(currentPageIndex);
  const [grouping, setGrouping] = useState(1);
  const [showGroupingPreview, setShowGroupingPreview] = useState(false);
  const [pageImages, setPageImages] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef(null);

  // Generate images from stage when dialog opens
  useEffect(() => {
    if (open && stageRef?.current) {
      generatePageImages();
    }
  }, [open, stageRef, pages]);

  const generatePageImages = async () => {
    if (!stageRef?.current) return;
    
    // For now, capture current stage state
    // In full multipage implementation, we'd iterate through pages
    const dataURL = stageRef.current.toDataURL({ pixelRatio: 2 });
    
    // Create array of page images (currently just one page)
    const images = pages.length > 0 
      ? pages.map(() => dataURL) 
      : [dataURL];
    
    setPageImages(images);
  };

  const handlePrevPage = () => {
    if (activePageIndex > 0) {
      setActivePageIndex(activePageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (activePageIndex < pageImages.length - 1) {
      setActivePageIndex(activePageIndex + 1);
    }
  };

  const handlePrint = async () => {
    setExporting(true);
    try {
      // Create print window with image
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const currentImage = pageImages[activePageIndex] || pageImages[0];
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${documentTitle}</title>
            <style>
              @page { size: ${pageOrientation === 'landscape' ? 'A4 landscape' : 'A4'}; margin: 10mm; }
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <img src="${currentImage}" onload="window.print(); window.close();" />
          </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (err) {
      console.error("Print error:", err);
    }
    setExporting(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      if (showGroupingPreview && grouping > 1) {
        await onExportGrouped?.(grouping, "pdf");
      } else {
        await onExportPDF?.();
      }
    } catch (err) {
      console.error("Export error:", err);
    }
    setExporting(false);
    onOpenChange(false);
  };

  const handleExportPNG = async () => {
    setExporting(true);
    try {
      await onExportPNG?.();
    } catch (err) {
      console.error("Export error:", err);
    }
    setExporting(false);
    onOpenChange(false);
  };

  const handleGridPageClick = (index) => {
    setActivePageIndex(index);
    setViewMode("carousel");
  };

  // Get grouping layout info
  const groupingConfig = EXPORT_GROUPINGS.find(g => g.id === grouping) || EXPORT_GROUPINGS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] max-h-[100vh] w-screen h-screen p-0 gap-0 border-0 rounded-none">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white border-b border-slate-700">
          <div className="flex items-center gap-4">
            <h2 className="font-bold text-lg">{documentTitle}</h2>
            <Badge variant="secondary" className="bg-slate-700 text-slate-200">
              {PAGE_FORMATS[pageFormat]?.label || "A4"} - {pageOrientation === "landscape" ? "Paysage" : "Portrait"}
            </Badge>
            {pageImages.length > 1 && (
              <Badge variant="outline" className="border-slate-600 text-slate-300">
                Page {activePageIndex + 1} / {pageImages.length}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center bg-slate-800 rounded-lg p-1 mr-4">
              <button
                onClick={() => setViewMode("carousel")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "carousel" 
                    ? "bg-slate-600 text-white" 
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Layers className="w-4 h-4 inline mr-1" />
                Carrousel
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "grid" 
                    ? "bg-slate-600 text-white" 
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Grid3X3 className="w-4 h-4 inline mr-1" />
                Grille
              </button>
            </div>

            {/* Zoom controls */}
            <div className="flex items-center gap-1 mr-4">
              <button
                onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom(Math.min(2, zoom + 0.25))}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-slate-700 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Preview area */}
          <div 
            ref={previewRef}
            className="flex-1 bg-slate-800 overflow-auto flex items-center justify-center p-8"
          >
            {viewMode === "carousel" ? (
              /* Carousel view */
              <div className="relative flex items-center justify-center w-full h-full">
                {/* Navigation arrows */}
                {pageImages.length > 1 && activePageIndex > 0 && (
                  <button
                    onClick={handlePrevPage}
                    className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm transition-colors"
                  >
                    <ChevronLeft className="w-8 h-8 text-white" />
                  </button>
                )}

                {/* Page preview */}
                <div 
                  className="bg-white shadow-2xl rounded-lg overflow-hidden transition-transform duration-200"
                  style={{ transform: `scale(${zoom})` }}
                >
                  {showGroupingPreview && grouping > 1 ? (
                    /* Grouping preview */
                    <GroupingPreview
                      images={pageImages}
                      grouping={grouping}
                      pageFormat={pageFormat}
                      pageOrientation={pageOrientation}
                    />
                  ) : (
                    /* Single page preview */
                    <img
                      src={pageImages[activePageIndex] || pageImages[0]}
                      alt={`Page ${activePageIndex + 1}`}
                      className="max-h-[70vh] object-contain"
                    />
                  )}
                </div>

                {/* Navigation arrows */}
                {pageImages.length > 1 && activePageIndex < pageImages.length - 1 && (
                  <button
                    onClick={handleNextPage}
                    className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm transition-colors"
                  >
                    <ChevronRight className="w-8 h-8 text-white" />
                  </button>
                )}

                {/* Page indicators */}
                {pageImages.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {pageImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActivePageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === activePageIndex
                            ? "bg-white"
                            : "bg-white/40 hover:bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Grid view */
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-4 w-full">
                {pageImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => handleGridPageClick(index)}
                    className={`group relative bg-white rounded-lg shadow-lg overflow-hidden transition-all hover:shadow-xl hover:scale-105 ${
                      index === activePageIndex ? "ring-4 ring-indigo-500" : ""
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Page ${index + 1}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-slate-900/80 text-white text-xs px-2 py-1 rounded">
                      Page {index + 1}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar - Export options */}
          <div className="w-80 bg-slate-900 border-l border-slate-700 p-4 flex flex-col gap-4 overflow-y-auto">
            <h3 className="font-bold text-white text-lg">Options d'export</h3>

            {/* Grouping toggle */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-300">Grouper les pages</span>
                <button
                  onClick={() => setShowGroupingPreview(!showGroupingPreview)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    showGroupingPreview ? "bg-indigo-500" : "bg-slate-600"
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      showGroupingPreview ? "left-7" : "left-1"
                    }`}
                  />
                </button>
              </div>

              {showGroupingPreview && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-400">Nombre par page A4</p>
                  <div className="grid grid-cols-5 gap-2">
                    {EXPORT_GROUPINGS.map((g) => (
                      <button
                        key={g.id}
                        onClick={() => setGrouping(g.id)}
                        className={`py-2 rounded-lg text-sm font-bold transition-all ${
                          grouping === g.id
                            ? "bg-indigo-500 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {g.id}
                      </button>
                    ))}
                  </div>

                  {/* Visual grouping preview */}
                  <div className="mt-4 p-3 bg-slate-900 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Apercu de la disposition</p>
                    <GroupingLayoutPreview 
                      cols={groupingConfig.cols} 
                      rows={groupingConfig.rows} 
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Export buttons */}
            <div className="space-y-3">
              <Button
                onClick={handlePrint}
                disabled={exporting}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>

              <Button
                onClick={handleExportPDF}
                disabled={exporting}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                {showGroupingPreview && grouping > 1 
                  ? `Telecharger PDF (${grouping}/page)` 
                  : "Telecharger PDF"
                }
              </Button>

              <Button
                onClick={handleExportPNG}
                disabled={exporting}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Telecharger PNG
              </Button>
            </div>

            {/* Keyboard shortcuts hint */}
            <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-700">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">Echap</kbd> pour fermer
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * GroupingPreview - Shows how pages will look when grouped
 */
function GroupingPreview({ images, grouping, pageFormat, pageOrientation }) {
  const config = EXPORT_GROUPINGS.find(g => g.id === grouping) || EXPORT_GROUPINGS[0];
  
  // Calculate grid dimensions
  const isLandscape = pageOrientation === "landscape";
  const containerStyle = {
    width: isLandscape ? "600px" : "424px",
    height: isLandscape ? "424px" : "600px",
    padding: "12px",
  };

  return (
    <div 
      className="bg-white rounded-lg grid gap-2" 
      style={{
        ...containerStyle,
        gridTemplateColumns: `repeat(${config.cols}, 1fr)`,
        gridTemplateRows: `repeat(${config.rows}, 1fr)`,
      }}
    >
      {Array.from({ length: grouping }).map((_, index) => (
        <div
          key={index}
          className="border border-slate-200 rounded overflow-hidden bg-slate-50 flex items-center justify-center"
        >
          {images[index % images.length] ? (
            <img
              src={images[index % images.length]}
              alt={`Preview ${index + 1}`}
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="text-slate-300 text-xs">Page {index + 1}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * GroupingLayoutPreview - Visual representation of page layout
 */
function GroupingLayoutPreview({ cols, rows }) {
  return (
    <div 
      className="aspect-[210/297] bg-white rounded border border-slate-600 p-1.5 grid gap-1"
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
    >
      {Array.from({ length: cols * rows }).map((_, index) => (
        <div
          key={index}
          className="bg-indigo-100 border border-indigo-300 rounded-sm flex items-center justify-center"
        >
          <span className="text-[8px] text-indigo-600 font-bold">{index + 1}</span>
        </div>
      ))}
    </div>
  );
}

export default ExportPreviewDialog;
